/**
 * Courses Page for trAInedup.ai
 * Displays course catalog with filtering and search functionality
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Clock,
  Users,
  Star,
  BookOpen,
  Play,
  Grid,
  List,
  Heart,
  CheckCircle,
} from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { FeatureBadge } from '../components/common/SubscriptionGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { courseService } from '../services/firebase/courses';
import { learningPlansService } from '../services/firebase/learningPlans';
import { Course, CourseDifficulty, CourseFilters } from '../types/course';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData, createBreadcrumbSchema } from '../components/seo/StructuredData';

/**
 * Courses page component
 * Displays course catalog with filtering, search, and enrollment
 */
export const CoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { canAccessTier, getCurrentTier } = useSubscription();
  const { user, isAuthenticated } = useAuth();
  
  // Get current tier for access checks
  const currentTier = getCurrentTier();
  const isFreePlan = currentTier === 'free';
  const isTwelveMonthUser = currentTier === 'twelve_month_package';
  
  // State
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [completedCourses, setCompletedCourses] = useState<Set<string>>(new Set());
  const [courseProgress, setCourseProgress] = useState<Map<string, any>>(new Map());
  // Pagination state
  const [pageSize, setPageSize] = useState<number>(50);

  // const [pendingCourseId, setPendingCourseId] = useState<string | null>(null);
  
  // Track the latest in-flight progress fetch to avoid race conditions
  const progressFetchTokenRef = useRef<symbol | null>(null);

  // Determine user access level (hasCompletedAssessment and free/paid status)
  const hasCompletedAssessment = (user as any)?.skillsAssessmentCompleted || false;



  const [hasMore, setHasMore] = useState<boolean>(false);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [pageStack, setPageStack] = useState<any[]>([]);


  /**
   * Load courses with current filters and fetch completion status (with pagination)
   * Handles 4 user states:
   * 1. Non-logged-in: Show only free courses
   * 2. Free user without assessment: Show only free courses
   * 3. Free user with assessment: Show free courses + paid courses in learning plan
   * 4. Paid user: Show all courses (or only learning plan courses based on toggle)
   */
  const loadCourses = useCallback(
    async (opts?: { startAfterDoc?: any; reset?: boolean }) => {
      try {
        setLoading(true);

        let coursesToShow: Course[] = [];
        let paginationHasMore = false;
        let paginationLastDoc: any = null;

        if (!isAuthenticated) {
          // STATE 1: NON-LOGGED-IN USERS
          // Show only free courses
          const filters: CourseFilters = {
            searchTerm: searchTerm || undefined,
            isFree: true,
            status: 'published',
          };
          const { courses: fetchedCourses } = await courseService.getCourses(
            filters,
            { pageSize: 1000 }
          );
          coursesToShow = fetchedCourses;
        } else {
          // LOGGED-IN USERS
          // Determine if we should restrict to free courses only
          // twelve_month_package users should see all courses
          const shouldShowOnlyFree = isFreePlan && !hasCompletedAssessment;

          if (shouldShowOnlyFree) {
            // Show only free courses
            const filters: CourseFilters = {
              searchTerm: searchTerm || undefined,
              status: 'published',
              isFree: true,
            };
            
            const { courses: fetchedCourses, lastDoc: newLastDoc, hasMore: newHasMore } = await courseService.getCourses(
              filters,
              { 
                pageSize, 
                startAfterDoc: opts?.startAfterDoc 
              }
            );
            coursesToShow = fetchedCourses;
            paginationHasMore = newHasMore;
            paginationLastDoc = newLastDoc;
          } else {
            // User has completed assessment OR is on paid tier (including twelve_month_package)
            // twelve_month_package users should see ALL courses
            // Other users: Show Free + Learning Plan courses
            
            if (isTwelveMonthUser) {
              // Twelve-month package users: Show ALL published courses
              const filters: CourseFilters = {
                searchTerm: searchTerm || undefined,
                status: 'published',
              };
              
              const { courses: fetchedCourses } = await courseService.getCourses(
                filters,
                { pageSize: 1000 }
              );
              coursesToShow = fetchedCourses;
              paginationHasMore = false;
              paginationLastDoc = null;
            } else {
              // Other logged-in users: Show Free + Learning Plan courses ONLY
            
            // 1. Get Learning Plan IDs
            const learningPlanCourses = await learningPlansService.getLearningPlanCourses();
            const learningPlanIds = new Set(learningPlanCourses.map(c => c.courseId));
            
            // 2. Fetch ALL published courses (matching search)
            // We fetch a large batch to perform client-side filtering for "Free OR LearningPlan"
            const filters: CourseFilters = {
              searchTerm: searchTerm || undefined,
              status: 'published',
            };
            
            const { courses: fetchedCourses } = await courseService.getCourses(
              filters,
              { pageSize: 1000 } // Fetch all to filter client side
            );
            
            // 3. Filter: Keep if Free OR in Learning Plan
              coursesToShow = fetchedCourses.filter(course => {
                const isFree = course.isFree || course.requiredSubscriptionTier === 'free';
                const inLearningPlan = learningPlanIds.has(course.id);
                return isFree || inLearningPlan;
              });
              
              // Since we fetched all (1000), we assume no more pages for this filtered view
              paginationHasMore = false; 
              paginationLastDoc = null;
            }
          }
        }

        if (opts?.reset) {
          setCourses(coursesToShow);
          setPageStack([]);
        } else {
          setCourses(prev => [...prev, ...coursesToShow]);
        }
        
        setHasMore(paginationHasMore);
        setLastDoc(paginationLastDoc);

        // Fetch progress for displayed courses
        if (isAuthenticated && coursesToShow.length > 0) {
          const progressToken = Symbol('progressFetch');
          progressFetchTokenRef.current = progressToken;

          const progressPromises = coursesToShow.map(course =>
            courseService.getUserProgress(course.id).catch(err => {
              console.error(`Error fetching progress for course ${course.id}:`, err);
              return null;
            })
          );

          Promise.all(progressPromises)
            .then(progressResults => {
              if (progressFetchTokenRef.current !== progressToken) {
                return;
              }

              const completedSet = new Set<string>();
              const progressMap = new Map<string, any>();

              coursesToShow.forEach((course, index) => {
                const progress = progressResults[index];
                if (progress) {
                  progressMap.set(course.id, progress);
                  if (progress.completionPercentage === 100) {
                    completedSet.add(course.id);
                  }
                }
              });

              setCompletedCourses(completedSet);
              setCourseProgress(progressMap);
            })
            .catch(error => {
              console.error('Error loading course progress:', error);
            });
        }

      } catch (error) {
        console.error('Error loading courses:', error);
        setCourses([]);
        setHasMore(false);
        setLastDoc(null);
        setPageStack([]);
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, searchTerm, pageSize, isFreePlan, hasCompletedAssessment, isTwelveMonthUser]
  );

  /**
   * Load courses on component mount and filter changes
   */
  useEffect(() => {
    loadCourses({ reset: true });
  }, [loadCourses]);

  useEffect(() => {
    if (loading) return;
    if (typeof window !== 'undefined') {
      (window as any).__PRERENDER_READY__ = true;
    }
  }, [loading]);

  /**
   * Load user's favorites from learning plan
   */
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const learningPlanCourses = await learningPlansService.getLearningPlanCourses();
        const favoritesSet = new Set(learningPlanCourses.map(course => course.courseId));
        setFavorites(favoritesSet);
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    };
    loadFavorites();
  }, []);

  /**
   * Handle search with debouncing
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      loadCourses({ reset: true });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, loadCourses]);

  /**
   * Get difficulty badge color
   */
  const getDifficultyColor = (difficulty: CourseDifficulty) => {
    switch (difficulty) {
      case CourseDifficulty.BEGINNER:
        return 'bg-green-100 text-green-800';
      case CourseDifficulty.INTERMEDIATE:
        return 'bg-yellow-100 text-yellow-800';
      case CourseDifficulty.ADVANCED:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };
  const canAccessCourse = (course: Course) => {
    // Check tier access based on requiredSubscriptionTier
    // A course is free if requiredSubscriptionTier is 'free'
    const tierToCheck = course.requiredSubscriptionTier || 'free';
    return canAccessTier(tierToCheck as any);
  };

  /**
   * Toggle favorite status
   */
  const toggleFavorite = async (courseId: string) => {
    try {
      const newFavorites = new Set(favorites);
      const course = courses.find(c => c.id === courseId);
      
      if (favorites.has(courseId)) {
        // Remove from favorites and learning plan
        newFavorites.delete(courseId);
        await learningPlansService.removeCourseFromLearningPlan(courseId, 'catalog');
        // Also remove from course service favorites for backward compatibility
        await courseService.removeFavorite(courseId);
      } else {
        // Add to favorites and learning plan
        newFavorites.add(courseId);
        await learningPlansService.addCourseToLearningPlan(
          courseId,
          course?.title || 'Unknown Course',
          'catalog'
        );
        // Also add to course service favorites for backward compatibility
        await courseService.addFavorite(courseId);
      }
      setFavorites(newFavorites);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  /**
   * Handle starting a course
   */
  const handleStartCourse = async (courseId: string) => {
    try {
      const course = courses.find(c => c.id === courseId);
      if (course) {
        await learningPlansService.addCourseToLearningPlan(
          courseId,
          course.title,
          'catalog'
        );
        if (!favorites.has(courseId)) {
          setFavorites(prev => new Set(prev).add(courseId));
        }
      }
    } catch (error) {
      console.error('Error starting course:', error);
    }
  };



  /**
   * Course card component
   */
  const CourseCard: React.FC<{ course: Course }> = ({ course }) => {
    const hasAccess = canAccessCourse(course);
    const isFavorite = favorites.has(course.id);
    const isCompleted = completedCourses.has(course.id);

    // Determine course credit cost (for twelve month users)
    const courseCredit = course.isFree || course.requiredSubscriptionTier === 'free' ? 0 : 1;

    return (
      <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
        <div className="relative">
          {/* Credit Badge for Twelve Month Users */}
          {isTwelveMonthUser && (
            <div className="absolute top-3 right-3 z-20">
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${courseCredit === 0 ? 'bg-green-600 text-white' : 'bg-yellow-500 text-white'}`}>
                {courseCredit === 0 ? '0 credits (Free)' : '1 credit'}
              </span>
            </div>
          )}
          {/* Course Thumbnail */}
          <div className="aspect-video bg-gradient-to-br from-primary-100 to-secondary-100 relative overflow-hidden">
            {course.thumbnail ? (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-primary-400" />
              </div>
            )}
            {/* Completed Badge */}
            {isCompleted && (
              <div className="absolute bottom-3 right-3">
                <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" /> Completed
                </span>
              </div>
            )}
            
            {/* Play Button Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Play className="w-6 h-6 text-primary ml-1" />
                </div>
              </div>
            </div>

            {/* Favorite Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleFavorite(course.id);
              }}
              className="absolute top-3 right-3 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all"
            >
              <Heart 
                className={`w-4 h-4 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
              />
            </button>

            {/* Access Badge */}
            {!hasAccess && (
              <div className="absolute top-3 left-3">
                <FeatureBadge tier={course.requiredSubscriptionTier as any} />
              </div>
            )}

            {/* Free Badge */}
            {course.isFree && (
              <div className="absolute top-3 left-3">
                <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Free
                </span>
              </div>
            )}
            
            {/* Learning Plan Badge */}
            {isFavorite && (
              <div className="absolute bottom-3 left-3">
                <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center">
                  <Star className="w-3 h-3 mr-1 fill-current" /> In Your Learning Plan
                </span>
              </div>
            )}
          </div>

          {/* Course Content */}
          <div className="p-6">
            {/* Category & Difficulty */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-primary">
                {course.category}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getDifficultyColor(course.difficulty)}`}>
                {course.difficulty}
              </span>
            </div>

            {/* Title & Description */}
            <h3 className="font-montserrat font-semibold text-lg text-neutral-800 mb-2 line-clamp-2">
              {course.title}
            </h3>
            <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
              {course.shortDescription}
            </p>

            {/* Course Stats */}
            <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDuration(course.estimatedDuration)}
                </div>
                <div className="flex items-center">
                  <BookOpen className="w-4 h-4 mr-1" />
                  {course.totalModules} modules
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  {course.enrollmentCount}
                </div>
                {course.averageRating > 0 && (
                  <div className="flex items-center">
                    <Star className="w-4 h-4 mr-1 text-yellow-500" />
                    {course.averageRating.toFixed(1)}
                  </div>
                )}
              </div>
            </div>

            {/* Action Button */}
            {(() => {
              // Non-authenticated users
              if (!isAuthenticated) {
                return (
                  <Link to="/register?redirect=/courses">
                    <Button variant="primary" fullWidth>
                      Register for free to access
                    </Button>
                  </Link>
                );
              }
              
              // Authenticated users
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const userTier = getCurrentTier();
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const hasCompletedAssessment = (user as any)?.skillsAssessmentCompleted || false;
              
              // Check if user has access to the course
              const canAccessPaidCourse = canAccessTier(course.requiredSubscriptionTier as any);
              const hasAccess = course.isFree || course.requiredSubscriptionTier === 'free' || canAccessPaidCourse;
              
              // STATE 1: User doesn't have access (free user viewing paid course)
              if (!hasAccess) {
                return (
                  <Link to="/pricing">
                    <Button variant="primary" fullWidth>
                      Upgrade to access
                    </Button>
                  </Link>
                );
              }
              
              // Get user progress for this course
              const progress = courseProgress.get(course.id);
              
              // STATE 2: User has not started the course (no progress)
              if (!progress) {
                // For twelve_month_package users and paid courses, show confirmation dialog
                if (isTwelveMonthUser && courseCredit === 1) {
                  // Start course immediately for twelve_month_package users (no dialog)
                  return (
                    <Link 
                      to={`/courses/${course.id}`}
                      onClick={() => handleStartCourse(course.id)}
                    >
                      <Button variant="primary" fullWidth>
                        Use 1 Credit & Start
                      </Button>
                    </Link>
                  );
                }
                // Default: start course immediately
                return (
                  <Link 
                    to={`/courses/${course.id}`}
                    onClick={() => handleStartCourse(course.id)}
                  >
                    <Button variant="primary" fullWidth>
                      Go to Course
                    </Button>
                  </Link>
                );
              }
              
              // STATE 3: User has started but not completed (in progress)
              // Check if course is completed
              const isCompleted = progress.completionPercentage === 100 || progress.status === 'completed';
              
              if (!isCompleted) {
                return (
                  <div className="flex flex-col gap-3">
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={async (e) => {
                        e.preventDefault();
                        // Navigate to first uncompleted module
                        const firstUncompleted = await courseService.getFirstUncompletedModule(course.id);
                        if (firstUncompleted) {
                          navigate(`/courses/${course.id}/modules/${firstUncompleted.id}`);
                        } else {
                          navigate(`/courses/${course.id}`);
                        }
                      }}
                    >
                      Continue Learning
                    </Button>
                    <Link 
                      to={`/courses/${course.id}`}
                      className="text-sm text-primary hover:underline block text-center"
                      onClick={() => handleStartCourse(course.id)}
                    >
                      Go to Course
                    </Link>
                  </div>
                );
              }
              
              // STATE 4: User has completed the course
              return (
                <div className="flex flex-col gap-3">
                  <Link to={`/certificate/${course.id}`}>
                    <Button variant="outline" fullWidth>
                      Download Completion Certificate
                    </Button>
                  </Link>
                  <Link
                    to={`/courses/${course.id}`}
                    onClick={() => handleStartCourse(course.id)}
                  >
                    <Button variant="primary" fullWidth>
                      Retake Course
                    </Button>
                  </Link>
                </div>
              );
            })()}
          </div>
        </div>
      </Card>
    );
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://trainedup.ai/' },
    { name: 'Courses', url: 'https://trainedup.ai/courses' },
  ]);

  return (
    <Layout>
      <SEOHead
        title="AI Courses - Practical AI Training for Everyone"
        description="Explore our growing library of step-by-step AI courses. Learn to use real AI tools to save time and work smarter. No tech background needed. Start your AI learning journey today."
        canonical="https://trainedup.ai/courses"
        keywords={['AI courses', 'AI training catalog', 'learn AI online', 'AI education', 'practical AI courses']}
      />
      <StructuredData data={breadcrumbSchema} />

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
              AI Courses
            </h1>
            <p className="text-base text-neutral-600 max-w-3xl">
              Explore our growing library of step-by-step courses that show you how to use 
              real AI tools to save time, work smarter, and get more done. No tech background needed.
            </p>
          </div>

          {/* Lesson Walkthrough Video - Only for Free Plan Users */}
          {isFreePlan && (
            <div className="mb-8">
              <Card className="p-6 bg-gradient-to-br from-primary-50 to-secondary-50">
                <div className="mb-4">
                  <h2 className="font-montserrat font-semibold text-xl text-neutral-800 mb-2">
                    📚 How to Get the Most Out of Your Courses
                  </h2>
                  <p className="text-neutral-600">
                    Watch this quick course overview to learn how to navigate lessons and maximize your learning experience.
                  </p>
                </div>
                <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg bg-black">
                  <video 
                    className="w-full h-full object-contain"
                    controls
                    loop
                    playsInline
                    preload="metadata"
                  >
                    <source 
                      src="https://firebasestorage.googleapis.com/v0/b/trainedup-a4915.firebasestorage.app/o/Video_lesson_walkthrough%2FLesson%20Walkthrough.mp4?alt=media&token=503a7e29-02b9-4885-86f8-2ce1b703f237" 
                      type="video/mp4" 
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </Card>
            </div>
          )}

          {/* Search and Layout Toggle */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              
              <div className="flex gap-2">
                <div className="flex border border-neutral-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Course Results */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : courses.length > 0 ? (
            <>
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
                {courses.map(course => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-8">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">Per page:</span>
                  <select
                    className="border border-neutral-300 rounded px-2 py-1 text-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      loadCourses({ reset: true });
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={pageStack.length === 0 || loading}
                    onClick={() => {
                      setPageStack(prev => {
                        const newStack = [...prev];
                        newStack.pop();
                        const prior = newStack[newStack.length - 1] || null;
                        loadCourses({ startAfterDoc: prior });
                        return newStack;
                      });
                    }}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!hasMore || loading}
                    onClick={() => {
                      if (lastDoc) {
                        loadCourses({ startAfterDoc: lastDoc });
                      }
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="font-semibold text-lg text-neutral-800 mb-2">
                No courses found
              </h3>
              <p className="text-neutral-600 mb-6">
                Try adjusting your search criteria.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                }}
              >
                Clear Search
              </Button>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

// Force commit to trigger deployment after logic fix
